//Node Modules
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil";
import { useForm, Controller } from "react-hook-form";
import _ from "lodash";

//GraphQL
import { API, graphqlOperation } from "aws-amplify";
import { updateZone } from "../../../graphql/mutations";
import { listTenantsByScheme } from "../../../graphql/queries-custom";

//BinaryForge Components
import { BfDialog } from "../../helpers";

//3rd Party Components
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { classNames } from "primereact/utils";

//Atoms
import { dialogAtomFamily, toastAtom } from "../../../atoms";
import { selectedUserSchemeAtom } from "../../../atoms/user";

//Helpers
import { useApiRequest } from "../../../helpers/Api";
import { findTreeNodeByKey, getAllChildZoneProperty, getAllParentZoneProperty } from "../../../helpers/Zones";
import { zoneNodesAtom, selectedZoneAtom } from "../../../atoms/zones";

//Other
const appElement = document.getElementsByClassName("appWrapper")[0];

const AssignTenantDialog = ({ zone }) => {
	//Hooks
	const { t } = useTranslation();
	const apiRequest = useApiRequest();

	// Recoil
	const [show, setShow] = useRecoilState(dialogAtomFamily("assignTenantDialog"));
	const selectedUserScheme = useRecoilValue(selectedUserSchemeAtom);
	const nodes = useRecoilValue(zoneNodesAtom);
	const setToast = useSetRecoilState(toastAtom);
	const setSelectedZone = useSetRecoilState(selectedZoneAtom);

	//Local
	const [tenants, setTenants] = useState([]);

	//On Load
	useEffect(() => {
		const listTenants = async () => {
			const cognitoUsers = await apiRequest("get", "/user", null, null);
			const {
				data: {
					userSchemeConfigBySchemeId: { items: tenantData },
				},
			} = await API.graphql(
				graphqlOperation(listTenantsByScheme, {
					schemeId: selectedUserScheme.id,
					filter: { role: { eq: "TENANT" } },
				})
			);
			const tenantUsers = _.intersectionBy(cognitoUsers, tenantData, "id");

			//Get any tenants assigned to parent zones in the node branch
			const parentTenants = _.flattenDeep(await getAllParentZoneProperty(zone, "tenant")).map((t) => ({ id: t }));
			//Get any tenants assigned to child zones in the node branch
			const childZone = await findTreeNodeByKey(nodes, zone.id);
			const childTenants = _.flattenDeep(await getAllChildZoneProperty(childZone, "tenant")).map((t) => ({
				id: t,
			}));

			const removeParents = _.differenceBy(tenantUsers, [...parentTenants, ...childTenants], "id");

			setTenants(removeParents);
		};

		if (show) {
			listTenants();
			if (zone.tenant) {
				reset({
					tenants: zone.tenant,
				});
			}
		}
	}, [show]);

	//React Hook Form
	const defaultValues = {
		tenants: [],
	};

	const {
		control,
		formState: { errors, isSubmitting },
		handleSubmit,
		reset,
	} = useForm({
		defaultValues,
		mode: "onTouched",
		reValidateMode: "onChange",
	});

	// Form Error Message
	const getFormErrorMessage = (name) => {
		return errors[name] && <span className="fontColour-error fontSize-small">{errors[name].message}</span>;
	};

	// Submit the Form
	const onSubmit = async (data) => {
		try {
			await API.graphql(graphqlOperation(updateZone, { input: { id: zone.id, tenant: data.tenants } }));
			//Reflect the change in the open zone detail so the Assigned Tenants count updates without a refetch
			setSelectedZone((current) => (current?.id === zone.id ? { ...current, tenant: data.tenants } : current));
			setShow(false);
			setToast({
				severity: "success",
				summary: t("zones.tenant.toast.successSummary"),
				detail: t("zones.tenant.toast.successDetail"),
			});
		} catch (err) {
			console.error("Assign Tenant Error ::", err);
			setToast({
				severity: "error",
				summary: t("zones.tenant.toast.errorSummary"),
				detail: t("zones.tenant.toast.errorDetail", { error: err?.errors?.[0]?.message ?? err?.message }),
			});
		}
	};

	// Footer
	const footer = (
		<>
			<Button label={t("common.action.cancel")} onClick={() => setShow(false)} />
			<Button
				label={t("zones.tenant.submit")}
				className="feature success"
				onClick={() => handleSubmit(onSubmit)()}
				disabled={isSubmitting}
			/>
		</>
	);

	return (
		<BfDialog id="assignTenantDialog" header={t("zones.tenant.header")} footer={footer}>
			<p className="marginBottom-small">
				{t("zones.tenant.message", {
					zone: zone?.label,
				})}
			</p>
			<form>
				<div className="formField">
					<label htmlFor="tenants">{t("zones.tenant.form.tenants.label")}</label>
					<Controller
						name="tenants"
						control={control}
						render={({ field: { ref, ...newField }, fieldState }) => (
							<MultiSelect
								appendTo={appElement}
								{...newField}
								inputRef={ref}
								filter
								options={tenants}
								optionValue="id"
								optionLabel="fullname"
								filterBy="fullname"
								placeholder={t("zones.tenant.form.tenants.placeholder")}
								onChange={(e) => newField.onChange(e.value)}
								className={classNames({ "p-error": fieldState.error })}
							/>
						)}
					/>
					{getFormErrorMessage("tenants")}
				</div>
			</form>
		</BfDialog>
	);
};

export default AssignTenantDialog;
